import { useState, useCallback } from 'react';
import {
  getBaziFromSolar,
  getBaziFromLunar,
  getLeapMonth,
  getLunarMonthDays,
} from '@/utils/lunarCalendar';
import type { BaziDetail } from '@/utils/lunarCalendar';
import { consumeUsage, getUsageInfo } from '@/utils/usageStorage';
import PaywallModal from '@/components/feature/PaywallModal';

interface Props {
  onCalculated: (detail: BaziDetail) => void;
}

const HOUR_OPTIONS = [
  { label: '子时 (23-01时)', index: 0 },
  { label: '丑时 (01-03时)', index: 1 },
  { label: '寅时 (03-05时)', index: 2 },
  { label: '卯时 (05-07时)', index: 3 },
  { label: '辰时 (07-09时)', index: 4 },
  { label: '巳时 (09-11时)', index: 5 },
  { label: '午时 (11-13时)', index: 6 },
  { label: '未时 (13-15时)', index: 7 },
  { label: '申时 (15-17时)', index: 8 },
  { label: '酉时 (17-19时)', index: 9 },
  { label: '戌时 (19-21时)', index: 10 },
  { label: '亥时 (21-23时)', index: 11 },
];

const LUNAR_MONTH_NAMES = ['正月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '冬月', '腊月'];

export default function BirthForm({ onCalculated }: Props) {
  const [calendarType, setCalendarType] = useState<'solar' | 'lunar'>('solar');

  // 公历
  const [solarYear, setSolarYear] = useState('');
  const [solarMonth, setSolarMonth] = useState('');
  const [solarDay, setSolarDay] = useState('');

  // 农历
  const [lunarYear, setLunarYear] = useState('');
  const [lunarMonth, setLunarMonth] = useState('');
  const [lunarDay, setLunarDay] = useState('');
  const [isLeapMonth, setIsLeapMonth] = useState(false);

  const [hourIndex, setHourIndex] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [showPaywall, setShowPaywall] = useState(false);
  const [usageInfo, setUsageInfo] = useState(getUsageInfo());

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 100 }, (_, i) => String(currentYear - i));
  const solarMonthOptions = Array.from({ length: 12 }, (_, i) => String(i + 1));

  // 根据农历年月计算有效天数
  const getLunarDayOptions = useCallback(() => {
    if (!lunarYear || !lunarMonth) return Array.from({ length: 30 }, (_, i) => String(i + 1));
    const y = parseInt(lunarYear);
    const m = parseInt(lunarMonth);
    const days = getLunarMonthDays(y, m, isLeapMonth);
    return Array.from({ length: days || 30 }, (_, i) => String(i + 1));
  }, [lunarYear, lunarMonth, isLeapMonth]);

  // 检查该年该月是否有闰月
  const leapMonthOfYear = useCallback(() => {
    if (!lunarYear) return 0;
    return getLeapMonth(parseInt(lunarYear));
  }, [lunarYear]);

  const handleCalculate = useCallback(() => {
    setErrorMsg('');

    // 检查使用次数
    const ok = consumeUsage();
    if (!ok) {
      setShowPaywall(true);
      return;
    }
    setUsageInfo(getUsageInfo());

    if (hourIndex === null) {
      setErrorMsg('请选择出生时辰');
      return;
    }

    if (calendarType === 'solar') {
      if (!solarYear || !solarMonth || !solarDay) {
        setErrorMsg('请填写完整的公历出生日期');
        return;
      }
      const result = getBaziFromSolar(
        parseInt(solarYear),
        parseInt(solarMonth),
        parseInt(solarDay),
        hourIndex
      );
      if (!result) {
        setErrorMsg('日期超出支持范围（1900-2100），请重新输入');
        return;
      }
      onCalculated(result);
    } else {
      if (!lunarYear || !lunarMonth || !lunarDay) {
        setErrorMsg('请填写完整的农历出生日期');
        return;
      }
      const result = getBaziFromLunar(
        parseInt(lunarYear),
        parseInt(lunarMonth),
        parseInt(lunarDay),
        isLeapMonth,
        hourIndex
      );
      if (!result) {
        setErrorMsg('农历日期无效，请检查是否有误（含闰月判断）');
        return;
      }
      onCalculated(result);
    }
  }, [calendarType, solarYear, solarMonth, solarDay, lunarYear, lunarMonth, lunarDay, isLeapMonth, hourIndex, onCalculated]);

  const leapM = leapMonthOfYear();

  return (
    <div className="w-full bg-background-100 rounded-xl border border-background-200 p-5 mb-5 animate-slide-up">
      {/* 剩余次数提示 */}
      {usageInfo.remaining > 0 && usageInfo.remaining <= 5 && (
        <div className="mb-4 text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-50 border border-accent-200">
            <i className="ri-error-warning-line text-accent-600 text-xs"></i>
            <span className="text-xs text-accent-700 font-medium">
              剩余 {usageInfo.remaining} 次
            </span>
          </div>
        </div>
      )}

      <h3 className="font-heading text-sm font-semibold text-foreground-800 mb-4">请输入您的出生信息</h3>

      {/* 公历/农历切换 */}
      <div className="flex bg-background-50 rounded-full p-1 border border-background-200 mb-4">
        {(['solar', 'lunar'] as const).map((type) => (
          <button
            key={type}
            onClick={() => setCalendarType(type)}
            className={`flex-1 py-1.5 rounded-full text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
              calendarType === type
                ? 'bg-primary-500 text-background-50 font-medium'
                : 'text-foreground-500 hover:text-foreground-800'
            }`}
          >
            {type === 'solar' ? '公历（阳历）' : '农历（阴历）'}
          </button>
        ))}
      </div>

      {/* 公历输入 */}
      {calendarType === 'solar' && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">出生年份（公历）</label>
            <select
              value={solarYear}
              onChange={(e) => setSolarYear(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 cursor-pointer"
            >
              <option value="">请选择年份</option>
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y} 年</option>
              ))}
            </select>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">月份</label>
              <select
                value={solarMonth}
                onChange={(e) => setSolarMonth(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                <option value="">月</option>
                {solarMonthOptions.map((m) => (
                  <option key={m} value={m}>{m} 月</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">日期</label>
              <select
                value={solarDay}
                onChange={(e) => setSolarDay(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                <option value="">日</option>
                {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => (
                  <option key={d} value={d}>{d} 日</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* 农历输入 */}
      {calendarType === 'lunar' && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">出生年份（农历）</label>
            <select
              value={lunarYear}
              onChange={(e) => { setLunarYear(e.target.value); setIsLeapMonth(false); setLunarDay(''); }}
              className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 cursor-pointer"
            >
              <option value="">请选择年份</option>
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y} 年</option>
              ))}
            </select>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">农历月</label>
              <select
                value={lunarMonth}
                onChange={(e) => { setLunarMonth(e.target.value); setIsLeapMonth(false); setLunarDay(''); }}
                className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                <option value="">月</option>
                {LUNAR_MONTH_NAMES.map((name, idx) => (
                  <option key={idx + 1} value={String(idx + 1)}>{name}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">农历日</label>
              <select
                value={lunarDay}
                onChange={(e) => setLunarDay(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                <option value="">日</option>
                {getLunarDayOptions().map((d) => (
                  <option key={d} value={d}>{d} 日</option>
                ))}
              </select>
            </div>
          </div>

          {/* 闰月选项（仅当该年有闰月且选了对应月份时显示） */}
          {lunarYear && lunarMonth && leapM > 0 && parseInt(lunarMonth) === leapM && (
            <div className="flex items-center gap-3 p-3 bg-accent-50 rounded-lg border border-accent-200">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isLeapMonth}
                  onChange={(e) => { setIsLeapMonth(e.target.checked); setLunarDay(''); }}
                  className="w-4 h-4 accent-primary-500 cursor-pointer"
                />
                <span className="text-xs text-accent-700">
                  {lunarYear} 年有闰{LUNAR_MONTH_NAMES[leapM - 1]}，是否为闰月出生？
                </span>
              </label>
            </div>
          )}

          <div className="bg-background-50 rounded-lg p-3 border border-background-200">
            <p className="text-xs text-foreground-400 leading-relaxed">
              提示：若选公历，系统将通过万年历自动转换为农历日期后推算八字，确保精准。
            </p>
          </div>
        </div>
      )}

      {/* 时辰 */}
      <div className="mt-4">
        <label className="block text-xs font-medium text-foreground-600 mb-1.5">出生时辰</label>
        <select
          value={hourIndex !== null ? String(hourIndex) : ''}
          onChange={(e) => setHourIndex(e.target.value === '' ? null : parseInt(e.target.value))}
          className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 cursor-pointer"
        >
          <option value="">请选择时辰</option>
          {HOUR_OPTIONS.map((h) => (
            <option key={h.index} value={h.index}>{h.label}</option>
          ))}
        </select>
      </div>

      {errorMsg && (
        <div className="mt-3 p-3 rounded-lg bg-accent-50 border border-accent-200">
          <p className="text-xs text-accent-700">{errorMsg}</p>
        </div>
      )}

      <button
        onClick={handleCalculate}
        className="w-full mt-5 py-3 rounded-xl bg-secondary-500 text-background-50 font-heading text-base font-bold hover:bg-secondary-600 transition-all duration-200 cursor-pointer whitespace-nowrap active:scale-[0.98]"
      >
        推算八字命格
      </button>

      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
      />
    </div>
  );
}